import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';

const RichTextEditor = ({ value, onChange, placeholder = 'Enter lesson content...' }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      const currentContent = editorRef.current.innerHTML;
      const newContent = value || '';
      if (currentContent === newContent) return;
      const isFocused = document.activeElement === editorRef.current;
      if (!isFocused || newContent === '') {
        editorRef.current.innerHTML = newContent;
      }
    }
  }, [value]);

  const formatText = (command, val = null) => {
    document.execCommand(command, false, val);
    handleContentChange();
    editorRef.current?.focus();
  };

  const insertLink = () => {
    const url = window.prompt('Enter the URL:');
    if (url) {
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      formatText('createLink', fullUrl);
      setTimeout(() => {
        if (editorRef.current) {
          const links = editorRef.current.getElementsByTagName('a');
          for (let link of links) {
            if (link.getAttribute('href') === fullUrl) {
              link.setAttribute('target', '_blank');
              link.setAttribute('rel', 'noopener noreferrer');
              link.classList.add('text-indigo-600', 'underline', 'font-bold');
            }
          }
          handleContentChange();
        }
      }, 10);
    }
  };

  const insertEmbed = () => {
    const code = window.prompt('Paste HTML embed code (e.g. <iframe ...>):');
    if (code) {
      // Force wrap in responsive container
      const finalCode = `<div class="responsive-embed shadow-lg">${code}</div><p><br></p>`;
      document.execCommand('insertHTML', false, finalCode);
      handleContentChange();
    }
  };

  const insertImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      const imgHtml = `<img src="${url}" class="rounded-2xl shadow-lg my-6" style="max-width: 100%; height: auto;" />`;
      document.execCommand('insertHTML', false, imgHtml);
      handleContentChange();
    }
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      if (content !== value) onChange(content);
    }
  };

  return (
    <div className="border border-gray-300 rounded-2xl overflow-hidden shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 transition-all">
      <div className="bg-gray-50 border-b border-gray-300 p-2.5 flex flex-wrap gap-1">
        <button type="button" onClick={() => formatText('bold')} className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all text-gray-600">
          <SafeIcon name="Bold" className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => formatText('italic')} className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all text-gray-600">
          <SafeIcon name="Italic" className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        <button type="button" onClick={insertLink} className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all text-indigo-600 font-bold flex items-center gap-1.5 px-3">
          <SafeIcon name="Link" className="w-4 h-4" />
          <span className="text-xs">Link</span>
        </button>
        <button type="button" onClick={insertEmbed} className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all text-purple-600 font-bold flex items-center gap-1.5 px-3">
          <SafeIcon name="Code" className="w-4 h-4" />
          <span className="text-xs">Embed</span>
        </button>
        <button type="button" onClick={insertImage} className="p-2 rounded-xl hover:bg-white hover:shadow-sm transition-all text-gray-600">
          <SafeIcon name="Video" className="w-4 h-4" />
        </button>
      </div>
      <div 
        ref={editorRef} 
        contentEditable 
        onInput={handleContentChange} 
        className="min-h-[300px] p-6 bg-white focus:outline-none prose prose-indigo max-w-none text-gray-800 leading-relaxed"
        data-placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;